// Offers + meetups query helpers. Owns direct supabase.from('item_interests'|'meetup_requests')
// access so chat components stay off the raw client.
import { supabase } from '@/integrations/supabase/client';

// ── item_interests ─────────────────────────────────────────────────────────

export interface OfferInterestSummary {
	status: string | null;
	offer_cents: number | null;
	counter_offer_cents: number | null;
	sender_id: string | null;
	item_id: string | null;
}

export const fetchOfferInterestSummary = async (
	interestId: string,
): Promise<OfferInterestSummary | null> => {
	const { data } = await supabase
		.from('item_interests')
		.select('status, offer_cents, counter_offer_cents, sender_id, item_id')
		.eq('id', interestId)
		.maybeSingle();
	return (data as any) ?? null;
};

export const respondToOfferInterest = async (
	interestId: string,
	patch: { status: 'accepted' | 'declined'; offer_cents?: number | null },
) => {
	const updates: any = { status: patch.status, responded_at: new Date().toISOString() };
	if (typeof patch.offer_cents === 'number') updates.offer_cents = patch.offer_cents;
	return supabase.from('item_interests').update(updates).eq('id', interestId);
};

export const counterOfferInterest = async (
	interestId: string,
	patch: { counter_offer_cents: number; counter_message?: string | null },
) =>
	supabase
		.from('item_interests')
		.update({
			status: 'countered',
			counter_offer_cents: patch.counter_offer_cents,
			counter_message: patch.counter_message ?? null,
			countered_at: new Date().toISOString(),
		})
		.eq('id', interestId);

export interface CreateOfferInterestInput {
	itemId: string;
	senderId: string;
	offerCents: number;
	message?: string | null;
	wantsTrade?: boolean;
}

export const createOfferInterest = async (input: CreateOfferInterestInput) =>
	supabase
		.from('item_interests')
		.insert({
			item_id: input.itemId,
			sender_id: input.senderId,
			offer_cents: input.offerCents,
			message: input.message ?? null,
			wants_trade: !!input.wantsTrade,
		})
		.select('id')
		.single();

// ── meetup_requests ────────────────────────────────────────────────────────

export const fetchMeetupRequestById = async (meetupId: string) => {
	const { data } = await supabase
		.from('meetup_requests')
		.select('*')
		.eq('id', meetupId)
		.maybeSingle();
	return (data as any) ?? null;
};

export const respondToMeetupRequest = async (
	meetupId: string,
	next: 'confirmed' | 'declined',
) =>
	supabase
		.from('meetup_requests')
		.update({ status: next, updated_at: new Date().toISOString() })
		.eq('id', meetupId);

export interface CreateMeetupRequestInput {
	interestId: string;
	proposedBy: string;
	proposedLocation: string;
	proposedAtIso: string;
	notes?: string | null;
}

export const createMeetupRequest = async (input: CreateMeetupRequestInput) =>
	supabase
		.from('meetup_requests')
		.insert({
			interest_id: input.interestId,
			proposed_by: input.proposedBy,
			proposed_location: input.proposedLocation,
			proposed_at: input.proposedAtIso,
			notes: input.notes ?? null,
			status: 'pending',
		})
		.select('id')
		.single();
